import React, { useState } from 'react';
import { Facebook, Twitter, Link2, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get the crawler-friendly share URL for social media
const getShareableUrl = (url) => {
  // If the URL is a relative path or contains certain routes, use the share API
  const path = url.replace(/^https?:\/\/[^\/]+/, '');
  
  // Map routes to their share endpoints
  if (path.includes('/swac-tv') || path === '/swac-tv') {
    return `${BACKEND_URL}/api/share/swac-tv`;
  }
  if (path.includes('/jsu-basketball')) {
    return `${BACKEND_URL}/api/share/page/jsu-basketball-2026-27`;
  }
  if (path.includes('/interactive/')) {
    const articleId = path.split('/interactive/')[1].split(/[?#]/)[0];
    return `${BACKEND_URL}/api/share/interactive/${articleId}`;
  }
  if (path.includes('/news/')) {
    const articleId = path.split('/news/')[1].split(/[?#]/)[0];
    return `${BACKEND_URL}/api/share/article/${articleId}`;
  }
  
  // For other pages, extract the slug and use universal share endpoint
  const slug = path.replace(/^\//, '').split('?')[0];
  if (slug) {
    return `${BACKEND_URL}/api/share/page/${slug}`;
  }
  
  // Fallback to original URL
  return url;
};

// Reddit Icon Component
const RedditIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
  </svg>
);

const ShareButtons = ({ 
  title, 
  url = typeof window !== 'undefined' ? window.location.href : '',
  description = '',
  variant = 'default', // 'default', 'compact', 'vertical'
  showLabels = true
}) => {
  const [copied, setCopied] = useState(false);
  
  // Get the shareable URL for social media crawlers
  const shareableUrl = getShareableUrl(url);
  const encodedUrl = encodeURIComponent(shareableUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description || title);
  
  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    reddit: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`
  };
  
  const handleCopyLink = async () => {
    try {
      // Copy the crawler-ready URL so pasted links keep the article title and image.
      await navigator.clipboard.writeText(shareableUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };
  
  const handleShare = (platform) => {
    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
  };
  
  const buttonBase = variant === 'compact' 
    ? 'p-2 rounded-full transition-all duration-200 hover:scale-110'
    : 'flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105';
  
  const containerClass = variant === 'vertical'
    ? 'flex flex-col gap-2'
    : 'flex flex-wrap items-center gap-2';

  return (
    <div className={containerClass} data-testid="share-buttons">
      {/* Facebook */}
      <button
        onClick={() => handleShare('facebook')}
        className={`${buttonBase} bg-[#1877F2] hover:bg-[#166FE5] text-white`}
        title="Share on Facebook"
        data-testid="share-facebook"
      >
        <Facebook className="w-5 h-5" />
        {showLabels && variant !== 'compact' && <span className="font-medium">Facebook</span>}
      </button>
      
      {/* X (Twitter) */}
      <button
        onClick={() => handleShare('twitter')}
        className={`${buttonBase} bg-black hover:bg-gray-800 text-white`}
        title="Share on X"
        data-testid="share-twitter"
      >
        <Twitter className="w-5 h-5" />
        {showLabels && variant !== 'compact' && <span className="font-medium">X</span>}
      </button>
      
      {/* Reddit */}
      <button
        onClick={() => handleShare('reddit')}
        className={`${buttonBase} bg-[#FF4500] hover:bg-[#E03D00] text-white`}
        title="Share on Reddit"
        data-testid="share-reddit"
      >
        <RedditIcon className="w-5 h-5" />
        {showLabels && variant !== 'compact' && <span className="font-medium">Reddit</span>}
      </button>
      
      {/* Copy Link */}
      <button
        onClick={handleCopyLink}
        className={`${buttonBase} ${copied ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'} text-white`}
        title="Copy Link"
        data-testid="share-copy-link"
      >
        {copied ? <Check className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
        {showLabels && variant !== 'compact' && <span className="font-medium">{copied ? 'Copied!' : 'Copy Link'}</span>}
      </button>
    </div>
  );
};

export default ShareButtons;
